import { validateDateRange } from "../../utils";
import { getSelectLimitDef, isBooleanLimit } from "./planLimits.util";

export const companyValidationRules = (values: any) => {
  const errors: Record<string, any> = {};
  const phoneRegex = /^\+[1-9]\d{0,3}\d{6,11}$/;
  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  const pinCodeRegex = /^\d{5,10}$/;
  const gstRegex = /^\d{2}[A-Z]{5}\d{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$$/;
  const panRegex = /^[A-Z]{5}\d{4}[A-Z]{1}$/;

  if (!values.name) errors.name = "Company name is required";
  else if (values.name.length < 3) errors.name = "Company name must be at least 3 characters";
  if (!values.primaryPhone) errors.primaryPhone = "Mobile number is required";
  else if (!phoneRegex.test(values.primaryPhone)) errors.primaryPhone = "Enter valid number (with country code)";
  if (!values.primaryEmail) errors.primaryEmail = "Email is required";
  else if (!emailRegex.test(values.primaryEmail)) errors.primaryEmail = "Enter a valid email";
  if (values.primaryAddress?.pinCode && !pinCodeRegex.test(values.primaryAddress?.pinCode))
    errors.primaryAddress = { pinCode: "Enter valid pin code" };
  if (values.taxDetails?.gstNumber && !gstRegex.test(values.taxDetails.gstNumber))
    errors.taxDetails = { gstNumber: "Enter valid GST number" };
  if (values.taxDetails?.panNumber && !panRegex.test(values.taxDetails.panNumber))
    errors.taxDetails = { panNumber: "Enter valid PAN number" };
  return errors;
};

export const adminValidationRules = (values: any, isUpdate?: boolean) => {
  const errors: Record<string, any> = {};
  const phoneRegex = /^\+[1-9]\d{0,3}\d{6,11}$/;
  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  const pinCodeRegex = /^\d{5,10}$/;

  if (!values.username) errors.username = "Username is required";
  else if (values.username.length < 5) errors.username = "Username must be at least 5 characters";
  if (!isUpdate && !values.password) errors.password = "Password is required";
  else if (!isUpdate && values.password && values.password.length < 8) errors.password = "Password must be at least 8 characters";
  if (!values.firstName) errors.firstName = "First name is required";
  else if (values.firstName.length < 3) errors.firstName = "First name must be at least 3 characters";
  if (!values.lastName) errors.lastName = "Last name is required";
  else if (values.lastName.length < 1) errors.lastName = "Last name must be at least 1 character";
  if (!isUpdate && !values.companyId) errors.companyId = "Please select a company";
  if (!values.primaryPhone) errors.primaryPhone = "Mobile number is required";
  else if (!phoneRegex.test(values.primaryPhone)) errors.primaryPhone = "Enter valid number (with country code)";
  if (!values.primaryEmail) errors.primaryEmail = "Email is required";
  else if (!emailRegex.test(values.primaryEmail)) errors.primaryEmail = "Enter a valid email";
  if (values.primaryAddress?.pinCode && !pinCodeRegex.test(values.primaryAddress?.pinCode))
    errors.primaryAddress = { pinCode: "Enter valid pin code" };

  return errors;
};

const LIMIT_FIELD_LABELS: Record<string, string> = {
  maxUsers: "Max Users",
  maxProjects: "Max Projects",
  maxClients: "Max Clients",
  maxEmployees: "Max Employees",
  maxWorkers: "Max Workers",
  maxVendors: "Max Vendors",
  maxSubcontractors: "Max Subcontractors",
  maxAccounts: "Max Accounts",
  maxStorageMB: "Max Storage (MB)",
  allowProjectFullReport: "Allow Project Full Report",
  allowClientFullReport: "Allow Client Full Report",
  maxReportRangeMonths: "Report date range",
};

export const planValidationRules = (values: any) => {
  const errors: Record<string, any> = {};

  if (!values.name) errors.name = "Plan name is required";
  else if (values.name.length < 3) errors.name = "Plan name must be at least 5 characters";
  if (!values.billingCycle) errors.billingCycle = "Please select a billing cycle";
  if (!values.price) errors.price = "Price is required";
  else if (values.price <= 0) errors.price = "Price must be greater than 0";
  if (!values.startDate) errors.startDate = "Start date is required";
  if (!values.endDate) errors.endDate = "End date is required";

  const planDateErrors = validateDateRange({
    fieldName: "endDate",
    startDateKey: "startDate",
    endDateKey: "endDate",
    startDate: values.startDate,
    endDate: values.endDate,
  });

  if (planDateErrors.startDate) errors.startDate = planDateErrors.startDate;
  if (planDateErrors.endDate) errors.endDate = planDateErrors.endDate;

  if (!values.webAccess && !values.mobileAccess) {
    errors.platformAccess = "At least one platform (Web or Mobile) must be enabled";
  }

  if (values.planFeatures && Array.isArray(values.planFeatures)) {
    values.planFeatures.forEach((pf: any, index: number) => {
      if (pf.limits) {
        const included: string[] = pf.limits.includedFeatureCodes || [];
        const hidden: string[] = pf.limits.hiddenFeatureCodes || [];
        const overlap = included.filter((code) => hidden.includes(code));
        if (overlap.length > 0) {
          errors[`planFeatures[${index}].limits.hiddenFeatureCodes`] =
            `Features cannot be both included and always-on: ${overlap.join(", ")}`;
        }
        Object.entries(pf.limits).forEach(([key, value]) => {
          if (key === "includedFeatureCodes" || key === "hiddenFeatureCodes") return;
          if (isBooleanLimit(key)) {
            if (value !== undefined && typeof value !== "boolean") {
              const label = LIMIT_FIELD_LABELS[key] || key;
              errors[`planFeatures[${index}].limits.${key}`] = `${label} must be true or false`;
            }
            return;
          }
          const selectDef = getSelectLimitDef(key);
          if (selectDef) {
            if (value !== undefined && !selectDef.options?.some((option) => option.value === value)) {
              const label = LIMIT_FIELD_LABELS[key] || key;
              errors[`planFeatures[${index}].limits.${key}`] = `${label} must be one of the available options`;
            }
            return;
          }
          if (value !== undefined && value !== "") {
            const numValue = Number(value);
            if (isNaN(numValue) || numValue < 0 || !Number.isInteger(numValue)) {
              const label = LIMIT_FIELD_LABELS[key] || key;
              errors[`planFeatures[${index}].limits.${key}`] = `${label} must be a non-negative integer`;
            }
          }
        });
      }
    });
  }

  return errors;
};
